import { useState, useEffect, useRef, lazy, Suspense } from "react";
import "./styles.css";
import "./colorscheme.css";
import { useTheme } from "./context/ThemeContext";
import ThemeToggle from "./components/ThemeToggle";
import MediaCarousel from "./components/MediaCarousel";
import "./components/MediaCarousel.css";
import analytics from "./utils/analytics";
import { apiUrl } from "./utils/api";
import { useLanguage } from "./context/LanguageContext";
import LanguageSwitcher from "./components/LanguageSwitcher";
import { Link } from "react-router-dom";

const MarkdownRenderer = lazy(() => import("./components/MarkdownRenderer"));

// Media item interface
interface MediaItem {
  url: string;
  type: 'image' | 'video';
  sort: number;
  is_primary: boolean;
}

// Define the structure of a product based on the API response
interface Product {
  id: number;
  name: string;
  price: string; // Assuming price is a string from the API
  original_price?: string;
  stock_count: number;
  has_stock: boolean;
  image_url?: string; // Real image URL from database (for backward compatibility)
  media_urls?: string; // JSON string containing media array
  media_list?: MediaItem[]; // Parsed media list
  image?: string; // Generated placeholder or real image for display (legacy)
  mediaType?: 'image' | 'video' | null; // Type of media (legacy)
}

// More detailed product info for the popup
interface ProductDetail extends Product {
  description: string;
  category_name: string;
  sales_count: number;
  available_card_count?: number; // 实际可用卡密数量
  card_type?: string; // 卡密类型
  created_at: string;
}

// Pre-defined colors for product image placeholders
const placeholderColors = ["9B59B6", "42F7ED", "11F550", "F511B6"];

// Helper function to determine media type from URL
const getMediaType = (url: string | null | undefined): 'image' | 'video' | null => {
  if (!url) return null;
  const extension = url.split('.').pop()?.toLowerCase();
  if (['jpg', 'jpeg', 'png', 'gif', 'webp'].includes(extension || '')) {
    return 'image';
  }
  if (['mp4', 'webm', 'ogg', 'mov', 'avi'].includes(extension || '')) {
    return 'video';
  }
  return null;
};

type TranslateFn = (key: string, params?: Record<string, string | number>) => string;

// Product Card Component
const ProductCard = ({ product, onClick, t }: { product: Product; onClick: (e: React.MouseEvent<HTMLDivElement>) => void; t: TranslateFn; }) => {
  // Parse media list from product data
  const getMediaList = (): MediaItem[] => {
    // Try to parse media_urls first
    if (product.media_urls) {
      try {
        return JSON.parse(product.media_urls);
      } catch (e) {
        console.warn('Failed to parse media_urls:', e);
      }
    }

    // Fallback to image_url for backward compatibility
    if (product.image_url || product.image) {
      const url = product.image_url || product.image!;
      return [{
        url,
        type: url.match(/\.(mp4|webm|mov|avi)$/i) ? 'video' : 'image',
        sort: 0,
        is_primary: true
      }];
    }

    return [];
  };

  const mediaList = getMediaList();

  return (
    <div className="product-card" onClick={onClick}>
      <div className="product-media-container">
        <MediaCarousel
          media={mediaList}
          className="product-media-carousel"
          autoPlay={true}
          showNavigation={true}
          showArrows={false}
          showLargePlayButton={false}
          fitMode="cover"
        />
        <div className="product-overlay">
          <div className="product-info">
            <h2 className="product-name">{product.name}</h2>
            <div className="price-container">
              {product.original_price && parseFloat(product.original_price) > parseFloat(product.price) && (
                <p className="product-original-price">
                  ${parseFloat(product.original_price).toFixed(2)}
                </p>
              )}
              <p className="product-price">${parseFloat(product.price).toFixed(2)}</p>
            </div>
            <button
              className="add-to-cart-btn"
              disabled={!product.has_stock}
              onClick={(e) => {
                e.stopPropagation();
                if (product.has_stock) {
                  analytics.trackPurchaseIntent(product.id, product.name, parseFloat(product.price));
                }
              }}
            >
              {product.has_stock ? t("product.addToCart") : t("product.outOfStock")}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

const ProductDetailPopup = ({ product, style, onClose, t }: { product: ProductDetail; style: React.CSSProperties; onClose: () => void; t: TranslateFn; }) => {
  // Stop propagation to prevent closing the popup when clicking inside
  const handlePopupClick = (e: React.MouseEvent<HTMLDivElement>) => {
    e.stopPropagation();
  };

  // Parse media list from product data
  const getMediaList = (): MediaItem[] => {
    // Try to parse media_urls first
    if (product.media_urls) {
      try {
        return JSON.parse(product.media_urls);
      } catch (e) {
        console.warn('Failed to parse media_urls:', e);
      }
    }

    // Fallback to image_url for backward compatibility
    if (product.image_url) {
      return [{
        url: product.image_url,
        type: product.image_url.match(/\.(mp4|webm|mov|avi)$/i) ? 'video' : 'image',
        sort: 0,
        is_primary: true
      }];
    }

    return [];
  };

  const mediaList = getMediaList();

  return (
    <div className="popup-card" style={style} onClick={handlePopupClick}>
      <button onClick={onClose} className="popup-close-btn">×</button>
      <div className="popup-content">
        <div className="popup-details-container">
          <h2 className="popup-product-name">{product.name}</h2>
          <div className="popup-product-description">
            <Suspense fallback={<div>{t("common.loadingDescription")}</div>}>
              <MarkdownRenderer content={product.description} />
            </Suspense>
          </div>
          <div className="popup-product-meta">
            <span className="popup-product-category">{product.category_name}</span>
            <span className="popup-product-stock">
              {/* Fixed stock display logic for card products */}
              {product.has_stock
                ? product.card_type && product.available_card_count !== undefined
                  ? t("product.inStockCards", { count: product.available_card_count })
                  : t("product.inStockCount", { count: product.stock_count })
                : t("product.outOfStock")}
            </span>
          </div>
          <div className="popup-price-container">
            {product.original_price && parseFloat(product.original_price) > parseFloat(product.price) && (
              <p className="popup-original-price">
                ${parseFloat(product.original_price).toFixed(2)}
              </p>
            )}
            <p className="popup-current-price">${parseFloat(product.price).toFixed(2)}</p>
          </div>
          <button
            className="popup-add-to-cart-btn"
            disabled={!product.has_stock}
            onClick={() => {
              if (product.has_stock) {
                analytics.trackPurchaseIntent(product.id, product.name, parseFloat(product.price));
              }
            }}
          >
            {product.has_stock ? t("product.addToCart") : t("product.outOfStock")}
          </button>
        </div>
        <div className="popup-image-container">
          {mediaList.length > 0 ? (
            <MediaCarousel
              media={mediaList}
              className="popup-media-carousel"
              autoPlay={false}
              showNavigation={true}
              showArrows={true}
              showLargePlayButton={true}
              fitMode="contain"
              enableImageZoom={true}
            />
          ) : (
            <div className="no-media-placeholder">
              {t("media.none")}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export const HomePage = () => {
  const { theme } = useTheme();
  const { t } = useLanguage();
  const [products, setProducts] = useState<Product[]>([]);
  const [config, setConfig] = useState({
    site_name: "BeeMerce",
    site_footer: `© ${new Date().getFullYear()} BeeMerce. All rights reserved.`,
    site_logo: "",
    site_favicon: ""
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedProduct, setSelectedProduct] = useState<ProductDetail | null>(null);
  const [selectedStaticPage, setSelectedStaticPage] = useState<{ title: string; content: string } | null>(null);
  const [isPopupVisible, setPopupVisible] = useState(false);
  const [isStaticPopupVisible, setStaticPopupVisible] = useState(false);
  const [popupStyle, setPopupStyle] = useState({});
  const footerRef = useRef<HTMLElement>(null);
  const gridRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    document.body.className = theme === 'dark' ? 'dark-mode' : '';
  }, [theme]);

  useEffect(() => {
    if (config.site_favicon) {
      const link = (document.getElementById('dynamic-favicon') as HTMLLinkElement) || 
                   document.querySelector("link[rel*='icon']") as HTMLLinkElement || 
                   document.createElement('link');
      
      const isSvg = config.site_favicon.toLowerCase().endsWith('.svg');
      link.type = isSvg ? 'image/svg+xml' : 'image/x-icon';
      link.rel = 'shortcut icon';
      link.href = config.site_favicon;
      
      if (!document.getElementById('dynamic-favicon')) {
        link.id = 'dynamic-favicon';
        document.getElementsByTagName('head')[0].appendChild(link);
      }
    }
  }, [config.site_favicon]);

  useEffect(() => {
    const fetchConfig = async () => {
      try {
        const response = await fetch(apiUrl("/api/v1/common/config"));
        if (response.ok) {
          const result = await response.json();
          if (result.success) {
            setConfig({
              site_name: result.data.site_name,
              site_footer: result.data.site_footer,
              site_logo: result.data.site_logo,
              site_favicon: result.data.site_favicon
            });
          }
        }
      } catch (e) {
        console.error("Failed to fetch site config", e);
      }
    };

    const fetchProducts = async () => {
      try {
        setLoading(true);
        const response = await fetch(apiUrl(`/api/v1/product_list?page=1&limit=50`));
        if (!response.ok) {
          const errorText = await response.text();
          throw new Error(`HTTP error! status: ${response.status}, body: ${errorText}`);
        }
        const result = await response.json();
        
        const productsData = Array.isArray(result.data) ? result.data : result.data.data;

        if (!Array.isArray(productsData)) {
          console.error("Unexpected data format:", result);
          throw new Error("Product data is not in the expected array format.");
        }

        const productsWithImages = productsData.map((product: Product, index: number) => {
          const imageUrl = product.image_url || `https://placehold.co/300x300/${placeholderColors[index % placeholderColors.length]}/FFFFFF?text=${encodeURIComponent(product.name)}`;
          const mediaType = getMediaType(product.image_url) || 'image';

          return {
            ...product,
            image: imageUrl,
            mediaType,
          };
        });

        setProducts(productsWithImages);
        setError(null);
      } catch (e) {
        if (e instanceof Error) {
          setError(e.message);
        } else {
          setError("UNKNOWN_ERROR");
        }
      } finally {
        setLoading(false);
      }
    };

    fetchConfig();
    fetchProducts();
  }, []);

  const handleProductClick = async (e: React.MouseEvent<HTMLDivElement>, product: Product) => {
    // Track product view
    analytics.trackProductView(product.id, product.name);

    const cardRect = e.currentTarget.getBoundingClientRect();
    const footerTop = footerRef.current?.getBoundingClientRect().top || document.documentElement.clientHeight;
    const firstCard = gridRef.current?.querySelector('.product-card');
    const gridTop = firstCard?.getBoundingClientRect().top || cardRect.top;

    // Set initial state for animation (starts from the clicked card)
    setPopupStyle({
      top: `${cardRect.top}px`,
      left: `${cardRect.left}px`,
      width: `${cardRect.width}px`,
      height: `${cardRect.height}px`,
    });

    try {
      const response = await fetch(apiUrl(`/api/v1/product/${product.id}`));
      if (!response.ok) throw new Error('Failed to fetch product details.');
      const result = await response.json();
      setSelectedProduct(result.data);
      setPopupVisible(true);

      // Set final state for animation - ensure popup is always visible
      setTimeout(() => {
        const viewportHeight = window.innerHeight;
        const headerHeight = 80; // Approximate header height
        const availableHeight = viewportHeight - headerHeight - 40; // 40px buffer

        // Calculate optimal top position to keep popup in viewport
        const calculateOptimalTop = (preferredHeight: number) => {
          const headerOffset = headerHeight + 10; // 10px buffer from header

          // For mobile/small screens, position near top with small margin
          if (window.innerWidth <= 768) {
            return Math.max(headerOffset, viewportHeight * 0.02);
          }

          // For larger screens, try to use gridTop but ensure popup fits
          const maxAllowedTop = viewportHeight - preferredHeight - 20;
          const optimalTop = Math.max(headerOffset, Math.min(gridTop, maxAllowedTop));

          return optimalTop;
        };

        // Responsive positioning based on screen size
        if (window.innerWidth <= 768) {
          const popupHeight = Math.min(availableHeight, viewportHeight * 0.95);
          setPopupStyle({
            top: `${calculateOptimalTop(popupHeight)}px`,
            left: `1%`,
            width: `98%`,
            height: `${popupHeight}px`,
            transition: 'all 0.3s ease-in-out',
          });
        } else if (window.innerWidth <= 900) {
          const popupHeight = Math.min(availableHeight, viewportHeight * 0.9);
          setPopupStyle({
            top: `${calculateOptimalTop(popupHeight)}px`,
            left: `2.5%`,
            width: `95%`,
            height: `${popupHeight}px`,
            transition: 'all 0.3s ease-in-out',
          });
        } else if (window.innerWidth <= 1200) {
          const popupHeight = Math.min(availableHeight, footerTop - gridTop - 20);
          setPopupStyle({
            top: `${calculateOptimalTop(popupHeight)}px`,
            left: `5%`,
            width: `90%`,
            height: `${popupHeight}px`,
            transition: 'all 0.3s ease-in-out',
          });
        } else {
          const popupHeight = Math.min(availableHeight, footerTop - gridTop - 20);
          setPopupStyle({
            top: `${calculateOptimalTop(popupHeight)}px`,
            left: `10%`,
            width: `80%`,
            height: `${popupHeight}px`,
            transition: 'all 0.3s ease-in-out',
          });
        }
      }, 10); // Small delay to ensure transition happens

    } catch (err) {
      console.error("Error fetching product details:", err);
    }
  };

  const handleClosePopup = () => {
    setPopupVisible(false);
    setSelectedProduct(null);
  };

  const handleStaticPageClick = async (e: React.MouseEvent, slug: string) => {
    e.preventDefault();
    try {
      const response = await fetch(apiUrl(`/api/v1/common/pages/${slug}`));
      if (!response.ok) throw new Error('Failed to fetch page content');
      const result = await response.json();
      setSelectedStaticPage(result.data);
      setStaticPopupVisible(true);
    } catch (err) {
      console.error("Error fetching static page:", err);
    }
  };

  return (
    <div className="app-container">
      <header className="app-header floating-banner">
        <div className="header-brand">
          {config.site_logo && <img src={config.site_logo} alt={config.site_name} className="header-logo" />}
          <h1>{config.site_name}</h1>
        </div>
        <nav>
          <Link to="/">{t("nav.products")}</Link>
          <a href="#" onClick={(e) => handleStaticPageClick(e, 'about')}>{t("nav.about")}</a>
          <a href="#" onClick={(e) => handleStaticPageClick(e, 'contact')}>{t("nav.contact")}</a>
        </nav>
        <div className="header-controls">
          <LanguageSwitcher />
          <ThemeToggle />
        </div>
      </header>
      <main>
        {loading && <p>{t("common.loadingProducts")}</p>}
        {error && (
          <p>
            {t("common.errorPrefix", {
              message: error === "UNKNOWN_ERROR" ? t("common.errorUnknown") : error
            })}
          </p>
        )}
        {!loading && !error && (
          <div ref={gridRef} className="product-grid">
            {products.map((product) => (
              <ProductCard key={product.id} product={product} onClick={(e) => handleProductClick(e, product)} t={t} />
            ))}
          </div>
        )}
      </main>
      <footer ref={footerRef} className="app-footer">
        <p>{config.site_footer}</p>
      </footer>

      {isPopupVisible && selectedProduct && (
        <div className="popup-overlay" onClick={handleClosePopup}>
          <ProductDetailPopup product={selectedProduct} style={popupStyle} onClose={handleClosePopup} t={t} />
        </div>
      )}

      {isStaticPopupVisible && selectedStaticPage && (
        <div className="popup-overlay" onClick={() => setStaticPopupVisible(false)}>
          <div className="popup-card static-page-popup" onClick={(e) => e.stopPropagation()} style={{
            top: '10%',
            left: '10%',
            width: '80%',
            height: '80%',
            transition: 'all 0.3s ease-in-out',
            position: 'fixed',
            zIndex: 1000,
            overflowY: 'auto'
          }}>
            <button onClick={() => setStaticPopupVisible(false)} className="popup-close-btn">×</button>
            <div className="popup-content" style={{ display: 'block', padding: '40px' }}>
              <h2 className="text-3xl font-bold mb-6">{selectedStaticPage.title}</h2>
              <div className="prose dark:prose-invert max-w-none">
                <Suspense fallback={<div>{t("common.loadingDescription")}</div>}>
                  <MarkdownRenderer content={selectedStaticPage.content} />
                </Suspense>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};